// lib
import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import ADND from '@aytm/lib/adnd/ADND'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { DataContext } from '@aytm/app/stats/page/sidebar/filters/FiltersPanel'

// components
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { MultiStateMultiSelect } from 'primereact/multistatemultiselect'

import DropdownSlider from 'primereact-raytm-theme/DropdownSlider'
import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'

import { optionGroupTemplate } from '@aytm/app/stats/page/components/lib/templates'

import SidebarPopupLabel from '@aytm/app/stats/page/sidebar/components/SidebarPopupLabel'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/sidebar/filters/styles/FiltersPanel.module.scss'

const FiltersRowQuestion = ({ group, filter, filterValues, filterOptions }) => {
  const {
    root,
    features,
    state,
    dropdownsRef,
    onDNDStart,
    onDNDEnd,
    addQuestion,
    onScrollToQuestion,
    onBasicFilterChange,
    onResetBasicFilter,
    onShowFilterDropdown,
    onHideFilterDropdown
  } = useContext(DataContext)

  const { mode } = state

  const [values, setValues] = useState(null !== filterValues ? JSON.parse(filterValues) : filterValues)
  const [options, setOptions] = useState(filterOptions)

  const [restoreDisabled, setRestoreDisabled] = useState(false)

  const filterRef = useRef(null)

  const dndListItemRef = useRef()

  const filterDisabled = useMemo(
    () => features.filters.disabled || filter.disabled,
    [features.filters.disabled, filter.disabled]
  )
  const filterSelected = useMemo(
    () =>
      'advanced' == mode || 'radiobutton' == filter.type || filterDisabled
        ? false
        : 'basic' == mode
          ? 'sliderselect' == filter.type
            ? null !== values && `1-${options.length}` != values.join('-')
            : null !== values && (0 < values.length || 'button' == filter.type)
          : false,
    [mode, filter.type, values, options, filterDisabled]
  )

  const filterFlatOptions = useMemo(
    () =>
      ['multistatemultiselect', 'multiselect'].includes(filter.type) && filter.group
        ? options.flatMap(option => option.items)
        : options,
    [filter.type, filter.group, options]
  )

  const onSliderChange = useCallback(
    event => {
      const values = [Math.max(event.value[0], 1), Math.min(event.value[1], options.length)]
      values.sort((a, b) => a - b)

      setValues(values)
      // the slider ends a slide only for a pointer; a handle moved from the keyboard has no end, so
      // each step is staged as it lands, or the row would read as picked with nothing to apply
      if ('keydown' === event.originalEvent?.type) onBasicFilterChange(filter, values)
    },
    [options, filter, onBasicFilterChange]
  )

  const onFilterChange = useCallback(
    event => {
      switch (filter.type) {
        case 'sliderselect': {
          const values = [Math.max(event.value[0], 1), Math.min(event.value[1], options.length)]
          values.sort((a, b) => a - b)

          onBasicFilterChange(filter, values)
          break
        }
        case 'dropdown': {
          const values = [event.value]

          onBasicFilterChange(filter, values)
          break
        }
        case 'multiselect': {
          const values = event.value
          values.sort(
            (a, b) => filterFlatOptions.findIndex(v => v.value == a) - filterFlatOptions.findIndex(v => v.value == b)
          )

          onBasicFilterChange(filter, values)
          break
        }
        case 'multistatemultiselect': {
          const values = event.value
          values.sort(
            (a, b) =>
              filterFlatOptions.findIndex(v => v.value == a.value) -
              filterFlatOptions.findIndex(v => v.value == b.value)
          )

          onBasicFilterChange(filter, values)
          break
        }
        case 'button': {
          onBasicFilterChange(filter, null)
          break
        }
        default: {
          break
        }
      }
    },
    [filter, options, filterFlatOptions, onBasicFilterChange]
  )

  const fetchData =
    filter.draggable && !features.filters.disabled
      ? {
          key: (filter.id || filter.key).split('-')[1],
          model: null,
          windowUID: PageGlobals.WINDOW_UID
        }
      : null

  const dndListItem =
    filter.draggable && !features.filters.disabled
      ? ADND.sources
          .data({
            type: `${group.key}-${PageGlobals.WINDOW_UID}`,
            clearDragImage: false
          })
          .onStart(onDNDStart)
          .onEnd(onDNDEnd)
          .fetchData(() => fetchData)
      : null

  const onRestoreQuestion = () => {
    setRestoreDisabled(true)
    addQuestion(filter.id)
  }

  const onShowDropdown = () => {
    if (null === filterRef.current) return
    onShowFilterDropdown(filterRef.current)
  }

  const onHideDropdown = () => {
    if (null === filterRef.current) return
    onHideFilterDropdown(filterRef.current)
  }

  const itemTemplate = option =>
    option.disabled && option.icon == undefined ? (
      <span dangerouslySetInnerHTML={{ __html: option.label }}></span>
    ) : (
      <span className="p-item-label">
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.label }}></span>
      </span>
    )

  const labelTemplate = useCallback(
    value =>
      'sliderselect' == filter.type ? (
        <span
          dangerouslySetInnerHTML={{
            __html: options[Math.min(Math.max(value, 1), options.length) - 1].label
          }}
        ></span>
      ) : null,
    [filter.type, options]
  )

  const buttonLabelTemplate = useCallback(
    event => {
      let label = filter.title

      if ('sliderselect' == filter.type) {
        label =
          event.sliderProps != undefined && event.sliderProps.value != undefined && 2 <= event.sliderProps.value.length
            ? `${event.sliderProps.min}-${event.sliderProps.max}` == event.sliderProps.value.join('-')
              ? filter.title
              : `${filter.title}: ${options[event.sliderProps.value[0] - 1].label} &ndash; ${
                  options[event.sliderProps.value[1] - 1].label
                }`
            : filter.title
      } else if ('dropdown' == filter.type) {
        if (values != undefined && 0 < values.length) label = `${filter.title}: ${values[0]}`
      } else if (event != undefined && event.value != undefined) {
        if (
          (('multistatemultiselect' == filter.type && !event.value.some(item => !item.state)) ||
            'multiselect' == filter.type) &&
          filterFlatOptions.length == event.value.length
        ) {
          label = `${event.placeholder}: All`
        } else if (0 < event.value.length) {
          label =
            1 == event.value.length && ('multiselect' == filter.type || event.value[0].state)
              ? `${event.placeholder}: ${
                  filterFlatOptions.find(
                    o => o.value == ('multiselect' == filter.type ? event.value[0] : event.value[0][event.optionValue])
                  )[event.optionLabel]
                }`
              : `${event.placeholder}: Filtered to ${event.value.length}`
        }
      } else if (event != undefined) label = event.placeholder

      return (
        <>
          {filter.icon ? <i className={`icon-aytmfd-${filter.icon}`} /> : null}
          <SidebarPopupLabel root={root} text={filter.text} label={label} />
        </>
      )
    },
    [root, filter, values, options, filterFlatOptions]
  )

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    const _values = null !== filterValues ? JSON.parse(filterValues) : filterValues
    setValues(_values)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues])
  useEffect(() => {
    setOptions(filterOptions)
  }, [filterOptions])

  useEffect(() => {
    if (['dropdown', 'multistatemultiselect', 'multiselect'].includes(filter.type)) {
      dropdownsRef.current[filter.key] = filterRef
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      delete dropdownsRef.current[filter.key]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, group, filterRef, dropdownsRef.current]) // TODO

  const renderComponent = () => {
    switch (filter.type) {
      case 'sliderselect': {
        return (
          <DropdownSlider
            data-testid={`stat-filters-row-${filter.key}-dropdownslider`}
            data-selected={filterSelected}
            dropdownClassName={`p-dropdown-rounded${
              filter.draggable && !features.filters.disabled ? ' p-composite-right' : ''
            }`}
            range
            min={1}
            max={options.length}
            value={values}
            labelTemplate={labelTemplate}
            buttonLabelTemplate={buttonLabelTemplate}
            disabled={filterDisabled || !options.length}
            onSlideEnd={filterDisabled || !options.length ? null : onFilterChange}
            onChange={filterDisabled || !options.length ? null : onSliderChange}
          />
        )
      }
      case 'dropdown': {
        return (
          <Dropdown
            ref={filterRef}
            data-testid={`stat-filters-row-${filter.key}-dropdown`}
            data-selected={filterSelected}
            className={`p-dropdown-rounded${filter.draggable && !features.filters.disabled ? ' p-composite-right' : ''}`}
            placeholder={filter.title}
            value={values}
            options={options}
            optionValue="value"
            optionLabel="label"
            itemTemplate={itemTemplate}
            valueTemplate={buttonLabelTemplate}
            filter={filter && Array.isArray(options) && options.some(o => !o.disabled)}
            filterBy="search,label"
            filterPlaceholder="Search items"
            resetFilterOnHide
            filterInputAutoFocus={false}
            fixedDropdownDir="bottom"
            disabled={filterDisabled || !options.length}
            onChange={filterDisabled || !options.length ? null : onFilterChange}
            onShow={filterDisabled || !options.length ? null : onShowDropdown}
            onHide={filterDisabled || !options.length ? null : onHideDropdown}
          />
        )
      }
      case 'multiselect': {
        return (
          <MultiSelect
            ref={filterRef}
            data-testid={`stat-filters-row-${filter.key}-multiselect`}
            data-selected={filterSelected}
            className={`p-multiselect-rounded${filter.draggable && !features.filters.disabled ? ' p-composite-right' : ''}`}
            placeholder={filter.title}
            value={values}
            options={options}
            optionValue="value"
            optionLabel="label"
            optionGroupLabel={filter.group ? 'text' : null}
            optionGroupChildren={filter.group ? 'items' : null}
            optionGroupTemplate={filter.group ? optionGroupTemplate : null}
            itemTemplate={itemTemplate}
            buttonLabelTemplate={buttonLabelTemplate}
            panelHeaderTemplate={MultiSelectHeader}
            panelFooterTemplate={MultiSelectFooter}
            filter={filterFlatOptions.some(o => !o.disabled)}
            filterBy="search,label"
            filterPlaceholder="Search items"
            resetFilterOnHide
            fixedDropdownDir="bottom"
            disabled={filterDisabled || !options.length}
            onChange={filterDisabled || !options.length ? null : onFilterChange}
            onShow={filterDisabled || !options.length ? null : onShowDropdown}
            onHide={filterDisabled || !options.length ? null : onHideDropdown}
          />
        )
      }
      case 'multistatemultiselect': {
        return (
          <MultiStateMultiSelect
            ref={filterRef}
            data-testid={`stat-filters-row-${filter.key}-multistatemultiselect`}
            data-selected={filterSelected}
            className={`p-multiselect-rounded${filter.draggable && !features.filters.disabled ? ' p-composite-right' : ''}`}
            placeholder={filter.title}
            value={values}
            options={options}
            optionValue="value"
            optionLabel="label"
            optionGroupLabel={filter.group ? 'text' : null}
            optionGroupChildren={filter.group ? 'items' : null}
            optionGroupTemplate={filter.group ? optionGroupTemplate : null}
            itemTemplate={itemTemplate}
            buttonLabelTemplate={buttonLabelTemplate}
            panelHeaderTemplate={MultiSelectHeader}
            panelFooterTemplate={MultiSelectFooter}
            filter={filterFlatOptions.some(o => !o.disabled)}
            filterBy="search,label"
            filterPlaceholder="Search items"
            resetFilterOnHide
            fixedDropdownDir="bottom"
            disabled={filterDisabled || !options.length}
            onChange={filterDisabled || !options.length ? null : onFilterChange}
            onShow={filterDisabled || !options.length ? null : onShowDropdown}
            onHide={filterDisabled || !options.length ? null : onHideDropdown}
          />
        )
      }
      case 'button': {
        return (
          <div
            data-testid={`stat-filters-row-${filter.key}-button`}
            data-selected={filterSelected}
            className={`${style['filter-button']}${
              filter.draggable && !features.filters.disabled ? ' p-composite-right' : ''
            }${filterSelected ? ' p-inputwrapper-filled' : ''}${filterDisabled ? ' p-disabled' : ''}`}
            {...pressable(filterDisabled ? null : onFilterChange)}
          >
            {filter.icon ? <i className={`icon-aytmfd-${filter.icon}`} /> : null}
            <span className={style['filter-button-text']}>
              <SidebarPopupLabel root={root} text={filter.text} label={filter.title} />
            </span>
          </div>
        )
      }
      case 'none': {
        return (
          <div className={style['filter-none']}>
            {filter.icon ? <i className={`icon-aytmfd-${filter.icon}`} /> : null}
            <span className={style['filter-none-text']}>
              <SidebarPopupLabel root={root} text={filter.text} label={filter.title} />
            </span>
          </div>
        )
      }
      default: {
        return null
      }
    }
  }

  return (
    <div
      data-id={filter.key}
      data-testid={`stat-filters-row-${filter.key}`}
      data-type={filter.type}
      data-selected={filterSelected}
      data-disabled={filterDisabled}
      className={`${style['filter-row']} ${style[`offset-${filter.lvl || 0}`]}`}
    >
      {filterSelected && (
        <span
          data-testid={`stat-filters-row-${filter.key}-reset`}
          className={`${style['filter-reset']}${filterDisabled ? ' p-disabled' : ''}`}
          aria-label="Clear filter"
          title="Clear filter"
          {...pressable(filterDisabled ? null : () => onResetBasicFilter(filter))}
        >
          <i className="icon-aytmfd-x-v2" />
        </span>
      )}
      <div ref={dndListItemRef} className={style['filter-row-inner']}>
        <div className="p-fluid p-col-12">
          {'basic' == mode ? (
            renderComponent()
          ) : (
            <div
              className={`${style['filter-button']}${
                filter.draggable && !features.filters.disabled ? ' p-composite-right' : ''
              } p-disabled`}
            >
              {filter.icon ? <i className={`icon-aytmfd-${filter.icon}`} /> : null}
              <span className={style['filter-button-text']} dangerouslySetInnerHTML={{ __html: filter.title }}></span>
            </div>
          )}
        </div>
        {filter.draggable && !features.filters.disabled && (
          <span data-testid={`stat-filters-row-${filter.key}-handle`} className={style['filter-handle']}>
            <i className="icon-aytmfd-handle" />
          </span>
        )}
      </div>
      {filter.draggable && !features.filters.disabled && (
        <span
          data-testid={`stat-filters-row-${filter.key}-restore`}
          className={style['filter-restore']}
          aria-label="Restore question"
          title="Restore question"
          {...pressable(restoreDisabled ? null : onRestoreQuestion)}
        />
      )}
      {filter.scrollable && (
        <span
          data-id={filter.id}
          data-testid={`stat-filters-row-${filter.key}-scroll`}
          className={style['filter-scroll']}
          aria-label="Scroll to question"
          title="Scroll to question"
          {...pressable(() => onScrollToQuestion(filter.id))}
        />
      )}
    </div>
  )
}

FiltersRowQuestion.propTypes = {
  group: PropTypes.object,
  filter: PropTypes.object,
  filterValues: PropTypes.any,
  filterOptions: PropTypes.any
}

FiltersRowQuestion.displayName = 'FiltersRowQuestion'

export default FiltersRowQuestion
